import React, { useState, useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import CircularProgress from '@material-ui/core/CircularProgress'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { ToggleButtonGroup } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import SegmentedToggleButton from 'components/segmented-toggle-button'
import useCalendarStyles from '../calendar/calendar-styles'
import AcademyScheduleDateJump from './academy-schedule-date-jump'
import AcademyCalendarShell from './academy-calendar-shell'
import {
  VIEW_WEEK,
  VIEW_DAY,
  getScheduleToolbarLabel,
  startOfUsWeek,
} from '../schedule-date-ranges'
import { buildScheduleRange } from './academy-schedule-range-utils'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1, 2),
    width: '100%',
    marginBottom: theme.spacing(0.5),
    flexShrink: 0,
  },
  toolbarRight: {
    marginLeft: 'auto',
  },
  academyLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200,
  },
  calendarFrame: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  dataLayer: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  },
  dataLayerHidden: {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  loadingBar: {
    position: 'absolute',
    inset: 0,
    zIndex: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    background: 'rgba(255, 255, 255, 0.72)',
    pointerEvents: 'none',
  },
  loadingLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}))

/**
 * Toolbar, date navigation, shell grid, and spinner. Range changes are pushed
 * to the data layer after paint so this tree never blocks on event unmount.
 */
function AcademyScheduleNavigation({
  academyName,
  children,
  onRangeChange,
  onRegisterLoadingHandler,
}) {
  const classes = useStyles()
  const calendarClasses = useCalendarStyles()
  const [viewMode, setViewMode] = useState(VIEW_WEEK)
  const [anchorDate, setAnchorDate] = useState(() => DateTime.local())
  const [pendingNavigation, setPendingNavigation] = useState(false)
  const [dataLoading, setDataLoading] = useState(true)

  useEffect(() => {
    onRegisterLoadingHandler?.(setDataLoading)
    return () => onRegisterLoadingHandler?.(null)
  }, [onRegisterLoadingHandler])

  useEffect(() => {
    if (!dataLoading && pendingNavigation) {
      setPendingNavigation(false)
    }
  }, [dataLoading, pendingNavigation])

  const headerLabel = useMemo(
    () => getScheduleToolbarLabel(viewMode, anchorDate),
    [viewMode, anchorDate],
  )

  const showLoading = pendingNavigation || dataLoading

  const commitNavigation = useCallback(
    (nextViewMode, nextAnchor) => {
      const range = buildScheduleRange(nextViewMode, nextAnchor)
      setViewMode(nextViewMode)
      setAnchorDate(nextAnchor)
      setPendingNavigation(true)
      requestAnimationFrame(() => {
        onRangeChange?.(range)
      })
    },
    [onRangeChange],
  )

  const goPrev = useCallback(() => {
    const next =
      viewMode === VIEW_WEEK
        ? anchorDate.minus({ days: 7 })
        : anchorDate.minus({ days: 1 })
    commitNavigation(viewMode, next)
  }, [anchorDate, commitNavigation, viewMode])

  const goNext = useCallback(() => {
    const next =
      viewMode === VIEW_WEEK
        ? anchorDate.plus({ days: 7 })
        : anchorDate.plus({ days: 1 })
    commitNavigation(viewMode, next)
  }, [anchorDate, commitNavigation, viewMode])

  const goToday = useCallback(() => {
    commitNavigation(viewMode, DateTime.local())
  }, [commitNavigation, viewMode])

  const handleJumpToDate = useCallback(
    picked => {
      const next =
        viewMode === VIEW_DAY ? picked.startOf('day') : startOfUsWeek(picked)
      commitNavigation(viewMode, next)
    },
    [commitNavigation, viewMode],
  )

  const handleViewModeChange = useCallback(
    (_, next) => {
      if (next == null) return
      const now = DateTime.local()
      const nextAnchor = next === VIEW_DAY ? now.startOf('day') : now
      commitNavigation(next, nextAnchor)
    },
    [commitNavigation],
  )

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        {academyName && (
          <Typography className={classes.academyLabel}>
            {academyName}
          </Typography>
        )}

        <Box className={classes.toolbarRight}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <IconButton size="small" onClick={goPrev} aria-label="Previous">
              <ChevronLeftIcon />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              onClick={goToday}
              style={{ textTransform: 'none', minWidth: 56 }}
            >
              Today
            </Button>
            <AcademyScheduleDateJump
              label={headerLabel}
              value={anchorDate}
              onJump={handleJumpToDate}
            />
            <IconButton size="small" onClick={goNext} aria-label="Next">
              <ChevronRightIcon />
            </IconButton>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              className={calendarClasses.segmentControl}
              aria-label="Calendar view"
            >
              <SegmentedToggleButton
                value={VIEW_WEEK}
                groupValue={viewMode}
                showCheck={false}
                className={calendarClasses.segmentButton}
                aria-label="Week view"
              >
                Week
              </SegmentedToggleButton>
              <SegmentedToggleButton
                value={VIEW_DAY}
                groupValue={viewMode}
                showCheck={false}
                className={calendarClasses.segmentButton}
                aria-label="Day view"
              >
                Day
              </SegmentedToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>

      <Box className={classes.calendarFrame}>
        {showLoading && (
          <AcademyCalendarShell
            anchorDate={anchorDate}
            isSingleDay={viewMode === VIEW_DAY}
          />
        )}
        <Box
          className={`${classes.dataLayer} ${
            showLoading ? classes.dataLayerHidden : ''
          }`}
        >
          {children}
        </Box>
        {showLoading && (
          <Box
            className={classes.loadingBar}
            aria-live="polite"
            aria-busy="true"
          >
            <CircularProgress size={36} thickness={4} />
            <Typography className={classes.loadingLabel}>
              Loading schedule…
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

AcademyScheduleNavigation.propTypes = {
  academyName: PropTypes.string,
  children: PropTypes.node,
  onRangeChange: PropTypes.func,
  onRegisterLoadingHandler: PropTypes.func,
}

export default AcademyScheduleNavigation
